import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import styles from './Solution.module.scss';
import { setupTransitionInTimeline } from './Solution.transitions';
import type { SolutionProps, SolutionRef } from './Solution.data';
import { useScrollTransition } from 'util/react-transition-component';

import DynamicComponent from '../../DynamicComponent';
import PageHeader from '../../organisms/page-header/PageHeader';
import IntroColumnText from '../intro-column-text/IntroColumnText';
import { useRouter } from 'next/router';
import Script from 'next/script';

export default function Solution({
  blok,
  canTransitionIn,
  ref,
  ...props
}: SolutionProps & { ref?: Ref<SolutionRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const router = useRouter();

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const { modules, ...data } = blok;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const description = data.intro_description?.content[0] as any;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${process.env.SITE_URL}`,
      },
      // / solutions doesnt exist
      {
        '@type': 'ListItem',
        position: 2,
        name: blok.title,
        item: `${process.env.SITE_URL}/${router.asPath}`,
      },
    ],
  };

  return (
    <>
      <section
        ref={elementRef}
        className={classNames(styles.solution, props.className)}
        {...sbEditable(blok)}
      >
        <PageHeader blok={data} canTransitionIn={canTransitionIn} />

        {((data?.intro_button && data?.intro_button?.length > 0 && data?.intro_button[0]) ||
          data.intro_title ||
          (description && description?.content?.length > 0)) && (
          <IntroColumnText
            blok={{
              _uid: data._uid,
              component: 'intro_column_text',
              title: data.intro_title,
              description: data.intro_description,
              buttons: data.intro_button,
            }}
          />
        )}

        {modules?.map((item) => (
          <DynamicComponent blok={item} key={item._uid} canTransitionIn={canTransitionIn} />
        ))}
      </section>

      <Script
        id="breadcrumbs-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </>
  );
}
